/**
 * Mixin para manejar pagos de OV con GetNet
 * Adaptado para sill-front desde la tienda
 */
import axios from '@/plugins/axios'

export default {
  data() {
    return {
      loadingPago: false,
      errorPago: null,
      pagoEnProceso: false,
    }
  },

  methods: {
    /**
     * Iniciar pago de abono para una OV
     * @param {String} ot - Número de OT
     * @param {String} rut - RUT del cliente
     * @param {String} tipoDocumento - Tipo de documento: 'boleta' o 'factura' (opcional, default: 'boleta')
     * @param {String} rutContribuyente - RUT del contribuyente (requerido si tipoDocumento es 'factura')
     * @returns {Promise<Object>} Resultado del inicio de pago
     */
    async iniciarPagoAbonoOV(
      ot,
      rut,
      tipoDocumento = 'boleta',
      rutContribuyente = null
    ) {
      this.loadingPago = true
      this.errorPago = null
      this.pagoEnProceso = true

      try {
        const body = {
          ot,
          rut,
          tipo_documento: tipoDocumento,
        }

        // Agregar RUT contribuyente si es factura
        if (tipoDocumento === 'factura' && rutContribuyente) {
          body.rut_contribuyente = rutContribuyente
        }

        const response = await axios.post(
          '/api/oportunidadventa/iniciar-pago-abono',
          body
        )

        const { processUrl, pago_id, monto, ov_id } = response.data

        console.log('Sesión de pago creada:', {
          pago_id,
          monto,
          ov_id,
          processUrl: processUrl ? 'OK' : 'ERROR',
        })

        if (processUrl) {
          // Guardar información en sessionStorage para recuperarla después
          sessionStorage.setItem(
            'pagoOVEnProceso',
            JSON.stringify({
              pago_id,
              ot,
              rut,
              monto,
              ov_id,
              tipo: 'ABONO',
              timestamp: Date.now(),
            })
          )

          return {
            success: true,
            data: response.data,
          }
        }

        throw new Error('No se recibió URL de pago de GetNet')
      } catch (error) {
        console.error('Error al iniciar pago de abono:', error)
        this.errorPago =
          error.response?.data?.error ||
          error.message ||
          'Error al iniciar pago'

        return {
          success: false,
          error: this.errorPago,
        }
      } finally {
        this.loadingPago = false
      }
    },

    /**
     * Iniciar pago del monto restante para una OV
     * @param {String} ot - Número de OT
     * @param {String} rut - RUT del cliente
     * @param {String} tipoDocumento - Tipo de documento: 'boleta' o 'factura' (opcional, default: 'boleta')
     * @param {String} rutContribuyente - RUT del contribuyente (requerido si tipoDocumento es 'factura')
     * @returns {Promise<Object>} Resultado del inicio de pago
     */
    async iniciarPagoRestanteOV(
      ot,
      rut,
      tipoDocumento = 'boleta',
      rutContribuyente = null
    ) {
      this.loadingPago = true
      this.errorPago = null
      this.pagoEnProceso = true

      try {
        const body = {
          ot,
          rut,
          tipo_documento: tipoDocumento,
        }

        // Agregar RUT contribuyente si es factura
        if (tipoDocumento === 'factura' && rutContribuyente) {
          body.rut_contribuyente = rutContribuyente
        }

        const response = await axios.post(
          '/api/oportunidadventa/iniciar-pago-restante',
          body
        )

        const { processUrl, pago_id, monto, ov_id, monto_restante } =
          response.data

        console.log('Sesión de pago restante creada:', {
          pago_id,
          monto,
          monto_restante,
          ov_id,
          processUrl: processUrl ? 'OK' : 'ERROR',
        })

        if (processUrl) {
          // Guardar información en sessionStorage para recuperarla después
          sessionStorage.setItem(
            'pagoOVEnProceso',
            JSON.stringify({
              pago_id,
              ot,
              rut,
              monto: monto_restante || monto,
              ov_id,
              tipo: 'RESTANTE',
              timestamp: Date.now(),
            })
          )

          return {
            success: true,
            data: response.data,
          }
        }

        throw new Error('No se recibió URL de pago de GetNet')
      } catch (error) {
        console.error('Error al iniciar pago restante:', error)
        this.errorPago =
          error.response?.data?.error ||
          error.message ||
          'Error al iniciar pago restante'

        return {
          success: false,
          error: this.errorPago,
        }
      } finally {
        this.loadingPago = false
      }
    },

    /**
     * Obtener información de la OV del cliente
     * @param {String} ot - Número de OT
     * @param {String} rut - RUT del cliente
     * @returns {Promise<Object>} Datos de la OV
     */
    async obtenerOVCliente(ot, rut) {
      try {
        const response = await axios.get('/api/oportunidadventa/ov-cliente', {
          params: { ot, rut },
        })

        return {
          success: true,
          data: response.data,
        }
      } catch (error) {
        console.error('Error al obtener OV cliente:', error)
        return {
          success: false,
          error: error.response?.data?.error || error.message,
        }
      }
    },

    /**
     * Redirigir a GetNet para realizar el pago
     * @param {String} processUrl - URL de GetNet
     */
    redirigirAGetNet(processUrl) {
      console.log('Redirigiendo a GetNet...')

      // Flag para controlar si se recibió el mensaje
      let mensajeRecibido = false
      let popupCheckInterval = null

      // Listener para recibir mensajes del popup
      const messageHandler = (event) => {
        // Validar origen del mensaje (opcional pero recomendado)
        console.log('Mensaje recibido:', {
          type: event.data?.type,
          data: event.data,
          origin: event.origin,
        })

        // Manejar tanto PAGO_RESULTADO (abono) como PAGO_RESTANTE_RESULTADO (restante)
        if (
          event.data &&
          (event.data.type === 'PAGO_RESULTADO' ||
            event.data.type === 'PAGO_RESTANTE_RESULTADO')
        ) {
          console.log('✓ Resultado de pago recibido correctamente:', event.data)

          // Marcar que se recibió el mensaje
          mensajeRecibido = true

          // Limpiar intervalo de verificación del popup
          if (popupCheckInterval) {
            clearInterval(popupCheckInterval)
            popupCheckInterval = null
          }

          // Remover listener
          window.removeEventListener('message', messageHandler)

          // Manejar el resultado del pago
          this.onPagoResultado(event.data)
        }
      }

      // Agregar listener
      window.addEventListener('message', messageHandler)

      // Abrir GetNet en popup
      const popup = window.open(
        processUrl,
        'getnet_payment_ot',
        'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no'
      )

      if (popup) {
        console.log('Popup de GetNet abierto exitosamente')

        // Detectar cuando se cierra el popup
        popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            console.log('Popup cerrado detectado')
            clearInterval(popupCheckInterval)
            popupCheckInterval = null

            // Dar un pequeño tiempo para que llegue el mensaje si está en tránsito
            setTimeout(() => {
              window.removeEventListener('message', messageHandler)

              if (!mensajeRecibido) {
                console.warn('⚠ Popup cerrado sin recibir mensaje postMessage')
                this.onPopupCerrado()
              } else {
                console.log(
                  '✓ Popup cerrado después de recibir mensaje correctamente'
                )
              }
            }, 500)
          }
        }, 500)
      } else {
        // Si el popup fue bloqueado, usar redirección directa
        console.warn(
          '⚠ Popup bloqueado por el navegador, usando redirección directa'
        )
        window.removeEventListener('message', messageHandler)
        window.location.href = processUrl
      }
    },

    /**
     * Manejar resultado del pago desde el popup
     */
    onPagoResultado(data) {
      console.log('Procesando resultado de pago:', data)

      const { estado, pagoId, boleta, monto, ot, rut } = data

      // Recuperar el tipo de pago desde sessionStorage
      const pagoInfo = sessionStorage.getItem('pagoOVEnProceso')
      let tipoPago = 'abono' // valor por defecto

      if (pagoInfo) {
        try {
          const { tipo } = JSON.parse(pagoInfo)
          tipoPago = tipo === 'RESTANTE' ? 'restante' : 'abono'
        } catch (e) {
          console.warn('Error al parsear pagoOVEnProceso:', e)
        }
      }

      // Actualizar estado del componente si existe
      if (this.pagoEnProceso !== undefined) {
        this.pagoEnProceso = false
      }

      // Limpiar sessionStorage
      sessionStorage.removeItem('pagoOVEnProceso')

      // Navegar a la página de resultado
      this.$router.push({
        path: `/ot/${ot}/${rut}/resultado-pago`,
        query: {
          estado,
          pago_id: pagoId,
          boleta: boleta || undefined,
          monto: monto || undefined,
          tipo_pago: tipoPago,
        },
      })
    },

    /**
     * Callback cuando se cierra el popup de GetNet sin completar
     */
    onPopupCerrado() {
      console.log('Popup cerrado sin recibir resultado del mensaje postMessage')

      // Actualizar estado del componente
      if (this.pagoEnProceso !== undefined) {
        this.pagoEnProceso = false
      }

      // Recuperar información del pago
      const pagoInfo = sessionStorage.getItem('pagoOVEnProceso')

      if (pagoInfo) {
        const { pago_id, ot, rut, tipo } = JSON.parse(pagoInfo)
        const tipoPago = tipo === 'RESTANTE' ? 'restante' : 'abono'

        console.log('Redirigiendo a resultado-pago con estado PENDIENTE:', {
          pago_id,
          ot,
          rut,
          tipoPago,
        })

        // Redirigir a resultado-pago en estado pendiente
        this.$router.push({
          path: `/ot/${ot}/${rut}/resultado-pago`,
          query: {
            estado: 'PENDIENTE',
            pago_id,
            tipo_pago: tipoPago,
          },
        })
      } else {
        // Si no hay información, mostrar mensaje y quedarse en la página
        console.warn('No se encontró información del pago en sessionStorage')

        if (this.$bvToast) {
          this.$bvToast.toast(
            'No pudimos confirmar el estado del pago. Por favor, recarga la página en unos momentos.',
            {
              title: 'Verificando Pago',
              variant: 'warning',
              solid: true,
              autoHideDelay: 5000,
            }
          )
        }
      }
    },

    /**
     * Verificar si una OV puede realizar pago
     * @param {Object} ovData - Datos de la OV
     * @returns {Boolean} True si puede pagar
     */
    puedePagarAbono(ovData) {
      if (!ovData) return false

      return (
        ovData.puede_pagar === true &&
        ovData.requiere_abono === true &&
        ovData.abono_requerido > 0 &&
        ovData.monto_total_final > 0 && // No se paga si el total es 0 (Garantía)
        !ovData.tiene_pago_exitoso
      )
    },

    /**
     * Formatear precio con separadores de miles
     * @param {Number} precio - Precio a formatear
     * @returns {String} Precio formateado
     */
    formatearPrecio(precio) {
      if (!precio) return '0'
      return new Intl.NumberFormat('es-CL').format(precio)
    },

    /**
     * Limpiar información de pago en proceso
     */
    limpiarPagoEnProceso() {
      sessionStorage.removeItem('pagoOVEnProceso')
      this.pagoEnProceso = false
      this.errorPago = null
    },
  },
}
